// Processing jobs: list with adaptive polling, retry, and the words and
// messages people see instead of internal job types and error codes.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type JobStatus = 'pending' | 'active' | 'completed' | 'failed';

export interface JobRow {
  id: string;
  jobType: string;
  status: JobStatus;
  progress: number;
  errorMessage: string | null;
  inputParams: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

/** Poll quickly while something is running, slowly otherwise. */
export function jobsRefetchInterval(jobs: JobRow[] | undefined): number {
  return jobs?.some((j) => j.status === 'pending' || j.status === 'active') ? 2000 : 15000;
}

export function useJobList() {
  return useQuery<{ jobs: JobRow[] }>({
    queryKey: ['jobs', 'list'],
    queryFn: () => api('/api/jobs?limit=100'),
    refetchInterval: (q) => jobsRefetchInterval(q.state.data?.jobs),
  });
}

export function useRetryJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ jobId: string }>(`/api/jobs/${id}/retry`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

const LABELS: Record<string, string> = {
  transcribe: 'Transcription',
  diarize: 'Speaker detection',
  waveform: 'Waveform',
  loudness: 'Loudness analysis',
  master: 'Mastering for podcast apps',
  transcode: 'Conversion',
  export: 'Export',
  'show-notes': 'AI show notes',
  chapters: 'AI chapters',
  'title-suggest': 'AI title ideas',
  'social-posts': 'AI social posts',
  'highlight-detect': 'AI highlights',
  hooks: 'AI hooks',
  'filler-detect': 'Filler & pause cleanup',
  'retake-detect': 'Retake detection',
  'image-gen': 'AI cover art',
  'hermes-simulate': 'Sponsorship simulation',
  discover: 'Sponsor discovery',
  'generate-outreach': 'Outreach drafts',
  'campaign-review': 'Campaign review',
  'retention-review': 'Sponsor retention review',
  'agent-chat': 'Sponsorship assistant',
  'send-outreach': 'Sending outreach email',
  'process-reply': 'Processing a sponsor reply',
};

export function jobLabel(jobType: string): string {
  return LABELS[jobType] ?? jobType.replace(/[-_]/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const ERRORS: Array<[RegExp, string]> = [
  [/stt_failed:.*empty transcript/i, 'No speech was found in this audio.'],
  [/stt_audio_too_long/i, 'This audio is too long to transcribe in one go (about 1.5 hours max for now).'],
  [/stt_timeout/i, 'Transcription took too long. Try again.'],
  [/rpaad_not_installed/i, 'PodcastWiz isn’t connected to your site’s AI. Reinstall the app from your site admin.'],
  [/rpaad_\w+_failed:(401|403)/i, 'Your site’s AI rejected the request. Check the app’s AI permissions in site admin.'],
  [/no_providers|rpaad_\w+_failed:503/i, 'No AI provider is available right now. Try again shortly.'],
  [/transcript_has_no_word_timings/i, 'This transcript has no word timings. Transcribe the audio again.'],
  [/transcription_not_found|transcript_has_no_segments/i, 'The transcript for this job no longer exists.'],
  [/ENOENT|Failed to fetch audio|s3_get_failed/i, 'The audio file couldn’t be read. Try re-uploading it.'],
  [/video_export_not_implemented/i, 'Video export isn’t available yet.'],
  [/Resend not configured/i, 'Email sending isn’t set up for this site.'],
  [/no_recipient_email/i, 'The sponsor has no contact email. Add one to the deal.'],
  [/Sponsorship profile not found/i, 'Set up your show’s sponsor profile first.'],
  [/fetch failed|ECONNREFUSED|ETIMEDOUT|ENOTFOUND/i, 'A service this job needs couldn’t be reached. Try again.'],
];

/** A readable reason for a failed job; the raw message is kept for support. */
export function jobError(message: string | null): string {
  if (!message) return 'The job failed.';
  for (const [re, text] of ERRORS) if (re.test(message)) return text;
  return 'The job failed.';
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}
